/**
 * collection-grid.js
 * ------------------------------------------------------------------
 * Comportement de la page collection : vue 2/3/4 colonnes, tiroir de
 * filtres, filtrage + tri + pagination en AJAX (rechargement partiel
 * via ?section_id=), et ajout rapide au panier depuis la grille.
 *
 * Tout est fait par délégation d'événements sur `document`, car le
 * contenu de #CollectionGridContainer est remplacé dynamiquement
 * (les éléments d'origine n'existent plus après un filtrage).
 * ------------------------------------------------------------------ */

(function () {
  'use strict';

  const SECTION_ID = 'main-collection';
  const VIEW_STORAGE_KEY = 'tcbh:collectionView';
  const CONTAINER_SELECTOR = '#CollectionGridContainer';

  let isFetching = false;

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    applyStoredView();

    document.addEventListener('click', handleClick);
    document.addEventListener('change', handleChange);
    document.addEventListener('input', handlePriceRangeInput);
  }

  /* ---------------------------------------------------------------- */
  /* Vue 2 / 3 / 4 colonnes                                            */
  /* ---------------------------------------------------------------- */

  function applyStoredView() {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (!stored) return;
    setView(stored, false);
  }

  function setView(view, persist) {
    const grid = document.querySelector('[data-grid]');
    if (grid) grid.setAttribute('data-view', view);

    document.querySelectorAll('.tcbh-view-btn').forEach((btn) => {
      const isActive = btn.dataset.view === view;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });

    if (persist) window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  }

  /* ---------------------------------------------------------------- */
  /* Tiroir de filtres                                                 */
  /* ---------------------------------------------------------------- */

  function openDrawer() {
    const drawer = document.querySelector('[data-facets-drawer]');
    const toggle = document.querySelector('[data-facets-toggle]');
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    const drawer = document.querySelector('[data-facets-drawer]');
    const toggle = document.querySelector('[data-facets-toggle]');
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove('is-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  function isDrawerOpen() {
    const drawer = document.querySelector('[data-facets-drawer]');
    return !!drawer && drawer.classList.contains('is-open');
  }

  /* ---------------------------------------------------------------- */
  /* Délégation des clics                                              */
  /* ---------------------------------------------------------------- */

  function handleClick(event) {
    const viewBtn = event.target.closest('.tcbh-view-btn');
    if (viewBtn) {
      setView(viewBtn.dataset.view, true);
      return;
    }

    if (event.target.closest('[data-facets-toggle]')) {
      openDrawer();
      return;
    }

    if (event.target.closest('[data-facets-close]')) {
      closeDrawer();
      return;
    }

    const clearLink = event.target.closest('[data-facets-clear]');
    if (clearLink) {
      event.preventDefault();
      fetchAndReplace(clearLink.href, { keepDrawerOpen: true });
      return;
    }

    const pageLink = event.target.closest('.tcbh-pagination a');
    if (pageLink) {
      event.preventDefault();
      fetchAndReplace(pageLink.href, { scrollToTop: true });
      return;
    }

    const sizeBtn = event.target.closest('[data-product-card-add]');
    if (sizeBtn && !sizeBtn.disabled) {
      event.preventDefault();
      addVariantToCart(sizeBtn);
    }
  }

  /* ---------------------------------------------------------------- */
  /* Délégation des changements (checkboxes, tri)                      */
  /* ---------------------------------------------------------------- */

  function handleChange(event) {
    if (event.target.matches('[data-facet-input]')) {
      applyFacetsForm();
      return;
    }

    if (event.target.matches('[data-sort-select]')) {
      applyFacetsForm();
      return;
    }

    if (event.target.matches('[data-price-min], [data-price-max]')) {
      syncPriceInputsToSliders(event.target);
      applyFacetsFormDebounced();
    }
  }

  /* ---------------------------------------------------------------- */
  /* Slider de prix (double thumb)                                     */
  /* ---------------------------------------------------------------- */

  let priceDebounceTimer = null;

  function handlePriceRangeInput(event) {
    if (!event.target.matches('[data-price-range]')) return;

    const facet = event.target.closest('[data-price-facet]');
    if (!facet) return;

    const minInput = facet.querySelector('[data-price-range="min"]');
    const maxInput = facet.querySelector('[data-price-range="max"]');
    let minVal = parseFloat(minInput.value);
    let maxVal = parseFloat(maxInput.value);

    if (minVal > maxVal) {
      if (event.target === minInput) minVal = maxVal;
      else maxVal = minVal;
      minInput.value = minVal;
      maxInput.value = maxVal;
    }

    const minNumber = facet.querySelector('[data-price-min]');
    const maxNumber = facet.querySelector('[data-price-max]');
    if (minNumber) minNumber.value = minVal;
    if (maxNumber) maxNumber.value = maxVal;

    applyFacetsFormDebounced();
  }

  function syncPriceInputsToSliders(input) {
    const facet = input.closest('[data-price-facet]');
    if (!facet) return;
    const isMin = input.matches('[data-price-min]');
    const slider = facet.querySelector(
      isMin ? '[data-price-range="min"]' : '[data-price-range="max"]'
    );
    if (slider) slider.value = input.value;
  }

  function applyFacetsFormDebounced() {
    window.clearTimeout(priceDebounceTimer);
    priceDebounceTimer = window.setTimeout(applyFacetsForm, 500);
  }

  /* ---------------------------------------------------------------- */
  /* Application des filtres / tri en AJAX                             */
  /* ---------------------------------------------------------------- */

  function applyFacetsForm() {
    const form = document.getElementById('FacetsForm');
    if (!form) return;

    const params = new URLSearchParams(new FormData(form));
    const url = `${window.location.pathname}?${params.toString()}`;
    fetchAndReplace(url, { keepDrawerOpen: isDrawerOpen() });
  }

  function fetchAndReplace(url, options) {
    options = options || {};
    if (isFetching) return;
    isFetching = true;

    const container = document.querySelector(CONTAINER_SELECTOR);
    if (container) container.setAttribute('aria-busy', 'true');

    const fetchUrl = buildSectionUrl(url);

    fetch(fetchUrl)
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContainer = doc.querySelector(CONTAINER_SELECTOR);
        const currentContainer = document.querySelector(CONTAINER_SELECTOR);

        if (newContainer && currentContainer) {
          currentContainer.replaceWith(newContainer);
          newContainer.removeAttribute('aria-busy');

          if (options.keepDrawerOpen) openDrawer();
          updateFilterCountBadge();
          setView(getCurrentView(), false);

          window.history.pushState({}, '', stripSectionParam(url));

          if (options.scrollToTop) {
            newContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }

          document.dispatchEvent(new CustomEvent('tcbh:collection:updated'));
        }
      })
      .catch((error) => {
        console.error('[collection-grid] échec du filtrage AJAX', error);
        window.location.href = url; // repli : navigation classique
      })
      .finally(() => {
        isFetching = false;
        if (container) container.removeAttribute('aria-busy');
      });
  }

  function buildSectionUrl(url) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}section_id=${SECTION_ID}`;
  }

  function stripSectionParam(url) {
    const u = new URL(url, window.location.origin);
    u.searchParams.delete('section_id');
    return u.pathname + (u.search ? u.search : '');
  }

  function getCurrentView() {
    const active = document.querySelector('.tcbh-view-btn.is-active');
    return active ? active.dataset.view : '3';
  }

  function updateFilterCountBadge() {
    const form = document.getElementById('FacetsForm');
    const badge = document.querySelector('[data-filter-count]');
    if (!form || !badge) return;

    const params = new URLSearchParams(new FormData(form));
    let count = 0;
    params.forEach((value, key) => {
      if (key.startsWith('filter.') && value) count += 1;
    });

    badge.textContent = String(count);
    badge.hidden = count === 0;
  }

  /* ---------------------------------------------------------------- */
  /* Ajout rapide au panier depuis la grille                           */
  /* ---------------------------------------------------------------- */

  function addVariantToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Ajout au panier refusé');
        return response.json();
      })
      .then(() => {
        button.classList.add('is-added');
        window.setTimeout(() => button.classList.remove('is-added'), 1500);
        refreshCartCount();
      })
      .catch((error) => {
        console.error('[collection-grid] échec ajout panier', error);
      })
      .finally(() => {
        button.disabled = false;
      });
  }

  function refreshCartCount() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
        });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      })
      .catch(() => {});
  }
})();
